import {createContext, useContext, useEffect, useState, useId, type ComponentProps, type ReactNode} from 'react';
import {Link} from 'react-router';
import {Permissions as P, readinessForBuildingLink, type BuildingOnboarding} from '@myfm/domain';
import {api} from '../api';
import {useAuth} from '../auth';
import {useCapability,useStaffSession} from './StaffSession';
import {freshOnboardingModel, rememberOnboardingModel, forgetOnboardingModels, onboardingModelFreshMs} from './onboarding-model-cache';
import {withBuildingScope} from './building-scope';
import {Icon, type IconName} from './Icon';
import {reviewedApplicationError} from './State';
import {isApiError} from '../api-error';
import './building-readiness.css';

type ReadinessRead = {building?: string | undefined; data?: BuildingOnboarding | undefined; error?: unknown; refresh: () => void; enabled: boolean};
const Context = createContext<ReadinessRead>({refresh: () => {}, enabled: false});
const pending = new Map<string, Promise<BuildingOnboarding>>();
let generation = 0;
let cacheIdentity: string | undefined;
if (typeof window !== 'undefined') window.addEventListener('canonical-record-saved', () => {generation++; pending.clear();});
function read(building: string,identity:string): Promise<BuildingOnboarding> {
  if(cacheIdentity!==identity){generation++;pending.clear();forgetOnboardingModels();cacheIdentity=identity;}
  const cached = freshOnboardingModel<BuildingOnboarding>(building);
  if (cached) return Promise.resolve(cached);
  const hit = pending.get(building);
  if (hit) return hit;
  const started = generation;
  const result: Promise<BuildingOnboarding> = api<BuildingOnboarding>(withBuildingScope(`/api/building-onboarding/${building}`, building)).then(model => {
    if (model.building.id !== building) throw reviewedApplicationError('The readiness information does not match this building.');
    if (started !== generation) throw reviewedApplicationError('Readiness changed while it was loading. Refresh the current records.');
    rememberOnboardingModel(building, model);
    return model;
  }).finally(() => {if (pending.get(building) === result) pending.delete(building);});
  pending.set(building, result);
  return result;
}

export function BuildingReadinessProvider({building, children}: {building: string | undefined; children: ReactNode}) {
  const {config,account} = useAuth(), allowed = useCapability(P.ViewClientOnboarding), session=useStaffSession();
  const identity=JSON.stringify([account?.homeAccountId??session?.displayName,session?.capabilities]);
  const enabled = Boolean(building && allowed && config?.buildingOnboardingEnabled);
  const [revision, setRevision] = useState(0);
  const [result, setResult] = useState<{building: string; identity:string; data?: BuildingOnboarding; error?: unknown}>();
  const refresh = () => {generation++; pending.clear(); forgetOnboardingModels(); setRevision(value => value + 1);};
  useEffect(() => {
    if (!enabled || !building) return;
    let current = true;
    // Refresh in place: clearing the model unmounts editors, loses unsaved
    // drafts and destroys mutation receipts before their responses are read.
    // The identity/building filter below still hides data across scope changes.
    void read(building,identity).then(data => {if (current) setResult({building, identity, data});}, error => {if (current) setResult(previous => ({building, identity, error, ...(previous?.building === building && previous.identity === identity && !(isApiError(error)&&[401,403,404].includes(error.statusCode)) ? {data:previous.data} : {})}));});
    return () => {current = false;};
  }, [building, enabled, revision, identity]);
  useEffect(() => {
    const focus = () => {if (document.visibilityState==='visible' && building && !freshOnboardingModel(building)) refresh();};
    window.addEventListener('canonical-record-saved', refresh);
    window.addEventListener('focus', focus);
    const timer = window.setInterval(focus, onboardingModelFreshMs);
    return () => {window.removeEventListener('canonical-record-saved', refresh); window.removeEventListener('focus', focus); window.clearInterval(timer);};
  }, [building]);
  const visible = enabled && result && result.building === building && result.identity===identity ? result : undefined;
  return <Context.Provider key={identity} value={{building, data: visible?.data, error: visible?.error, refresh, enabled}}>{children}</Context.Provider>;
}
export const useBuildingReadiness = () => useContext(Context);

/** No additional marks or coloured row backgrounds. The original icon retains its meaning. */
export function ReadinessIcon({name, href}: {name: IconName; href: string}) {
  const {data, enabled} = useBuildingReadiness();
  const readiness = data ? readinessForBuildingLink(data, href) : undefined;
  return <Icon name={name} className={enabled ? 'operational-readiness-icon' : undefined} data-readiness={enabled ? readiness?.state ?? 'unknown' : undefined}/>;
}

/** Status is a description, not part of the destination's name. Preserve familiar
 * keyboard/screen-reader navigation and expose meaning without relying on colour. */
export function ReadinessNavLink({to,children,...props}:Omit<ComponentProps<typeof Link>,'to'>&{to:string}){
  const {data,enabled}=useBuildingReadiness(),id=useId();
  const readiness=data?readinessForBuildingLink(data,to):undefined;
  const description=readiness?.label??'Readiness not assessed';
  const describedBy=[props['aria-describedby'],enabled?id:undefined].filter(Boolean).join(' ')||undefined;
  return <Link {...props} to={to} aria-describedby={describedBy} title={props.title??(enabled?description:undefined)}>{children}
    {enabled&&<span id={id} hidden className="sr-only">{description}</span>}
  </Link>;
}
